import React, { useState, useEffect, useRef } from "react";
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from "react-zoom-pan-pinch";

import ZoomButton from "./zoom-button";
import Legend from "./legend";
import QuadrantContainer from '../quadrant-container';
import Overlay from "../detail-overlay";
import CustomCursor from "./cursor";
import Indicator from "./indicator";
import Filter from '../filter';

import { useTranslation } from 'react-i18next';

import { Technologies, Technology } from "../../types/types";
import { transformWrapperConfig } from '../../../cnf/config';

import "../../css/app.css";
import "../../css/fonts.css";
import "../../css/custom-elements.css";

const ScaleContainer = ({ tags }: { tags: Technologies }) => {
    const [technology, setTechnology] = useState<Technology | null>(null);
    const transformWrapperRef = useRef<ReactZoomPanPinchRef>(null);
    const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: boolean; }>({});
    const [isFullScreen, setIsFullScreen] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            if (transformWrapperRef.current) {
                const wrapperInstance = transformWrapperRef.current;

                wrapperInstance.centerView();
            }
        };

        handleResize();

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        document.addEventListener('fullscreenchange', handleFullScreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullScreenChange);
        };
    }, []);

    const handleClick = (tech: Technology) => {
        setTechnology(tech);
    };

    const handleClose = () => {
        setTechnology(null);
    };

    let [scale, setScale] = useState(1);

    function handleScaleChange(event: any) {
        setScale(event.instance.transformState.scale);
    }

    const handleFilterClick = (filter: { [key: string]: boolean; }) => {
        let filtersToSet = filter;
        setSelectedFilters(filtersToSet);
    }

    const centerQuadrant = (timeToCenter: number) =>  {
        setTimeout(() => {
            if (transformWrapperRef.current) {
                const wrapperInstance = transformWrapperRef.current;
                wrapperInstance.centerView();
            }
        }, timeToCenter);
    }

    const goFullScreen = () => {
        setIsFullScreen(!isFullScreen);

        if (mainDivRef.current) {
            if (!isFullScreen) { // if not currently in full screen mode, enter full screen mode
                const fullscreenMethods = [
                    'requestFullscreen',
                    'mozRequestFullScreen',
                    'webkitRequestFullscreen',
                    'msRequestFullscreen'
                ];

                const fullscreenMethod = fullscreenMethods.find(method => (mainDivRef.current as any)[method]);

                if (fullscreenMethod) {
                    centerQuadrant(200);

                    (mainDivRef.current as any)[fullscreenMethod]();
                }
            } else { // if currently in full screen mode, exit full screen mode
                const exitFullscreenMethods = [
                    'exitFullscreen',
                    'mozCancelFullScreen',
                    'webkitExitFullscreen',
                    'msExitFullscreen'
                ];

                const exitFullscreenMethod = exitFullscreenMethods.find(method => (document as any)[method]);


                if (exitFullscreenMethod) {
                    centerQuadrant(200);

                    (document as any)[exitFullscreenMethod]();
                }
            }
        }
    };

    const handleFullScreenChange = () => {
        if (!document.fullscreenElement) {
            centerQuadrant(500);
        }
    };

    const mainDivRef = useRef<HTMLDivElement | null>(null);
    const { t } = useTranslation();

    return (
        <div>
            <div className={`main ${isFullScreen ? 'full-screen' : ''}`} ref={mainDivRef}>
            <CustomCursor/>
            <TransformWrapper
                {...transformWrapperConfig}
                ref={transformWrapperRef}
                initialScale={scale}
                onTransformed={(e) => handleScaleChange(e)}
            >
                {({ zoomIn, zoomOut }) => {
                    return (
                        <>
                            <div className={'scale-box'}>

                                <button className={isFullScreen ? 'toggle-full-screen-on' : 'toggle-full-screen'} onClick={goFullScreen}></button>

                                <Filter onButtonClick={handleFilterClick} />

                                <div className="zoom-menu">
                                    <ZoomButton
                                        type="in"
                                        disabled={scale === transformWrapperConfig.maxScale}
                                        mode={zoomIn}
                                    />
                                    <ZoomButton
                                        type="out"
                                        disabled={scale === transformWrapperConfig.minScale}
                                        mode={zoomOut}
                                    />
                                </div>

                                <Indicator/>

                                {technology && <Overlay tag={technology} onClose={handleClose} />}

                                <div className={'scale-wrapper'}>
                                    <TransformComponent>
                                        <div className={'transform-comp'}>
                                            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                                                <div>

                                                    <div
                                                        className="cross"
                                                        style={{
                                                            opacity: 0.20 / scale,
                                                        }}
                                                    ></div>

                                                    <QuadrantContainer filter={selectedFilters} scale={scale} tags={tags} handleClick={handleClick}/>
                                                </div>
                                            </div>
                                        </div>
                                    </TransformComponent>
                                </div>
                            </div>
                        </>
                    );
                }}
            </TransformWrapper>

            <Legend/>
        </div>

            <div className={'quadrant-overview-container'}>
                <div className={'quadrant-overview'}>
                    <button
                        className="open-quadrant-button"
                        onClick={goFullScreen}
                        role="button"
                        tabIndex={0}
                        aria-disabled={false}
                    >
                        {t('Quadrant öffnen')}
                    </button>
                </div>

                <div className={'quadrant-info-box'}>
                    <div className={'quadrant-info-icon'}></div>
                    <p>{t('Quadrant öffnen, um einzelne Technologien sehen zu können')}</p>
                </div>
            </div>
        </div>
    );
};

export default ScaleContainer;